// OTT Platform Utility Functions
// Centralized configuration for OTT platform detection and styling

import {
  PlayArrow,
  Movie,
  Tv,
  VideoLibrary,
  LiveTv,
  Theaters,
  OndemandVideo,
  SmartDisplay,
  PlayCircle,
  Cast,
  ConnectedTv,
  Star,
  Videocam,
  OpenInNew
} from '@mui/icons-material';

/**
 * OTT Platform Icon Component with PNG support
 * @param {Object} props - Component props
 * @param {string} props.platform - Platform name
 * @param {number} props.size - Icon size (default: 20)
 * @param {string} props.color - Icon color
 * @returns {React.Component} Icon component
 */
export function OTTIcon({ platform, size = 20, color = 'currentColor', ...props }) {
  // PNG icon mapping for specific platforms
  const pngIcons = {
    'Amazon Prime Video': '/Prime_1.png',
    'Netflix': '/Netflix_1.png',
    'Aha': '/Aha_1.png',
    'ZEE5': '/Z5_1.png',
    'SonyLIV': '/Sony_Liv_1.png',
    'Sun NXT': '/Sun_Nxt_1.png',
    'JioHotstar': '/JioHotstar_2025_1.png',
    'Hotstar': '/JioHotstar_2025_1.png',
    'ETV Win': '/ETV_Win_1.png',
    'JioCinema': '/JioCinema_1.png',
    'YouTube': '/Youtube_1.png',
    'MX Player': '/MX_1.png',
    'Voot': '/Voot_1.png',
    'Apple TV+': '/Apple_1.png',
    'Discovery+' : '/Discovery_1.png',
    'Lionsgate Play': '/Lionsgate_1.png',
    'Hungama Play': '/Hungama_1.png', 
    'Hoichoi': '/Hoichoi_1.png',
    'ShemarooMe': '/Shemaroo_1.png',
    'Chaupal': '/Chaupal_1.png'
  };
  
  // Material icon fallback mapping
  const iconMap = {
    'Netflix': Movie, //done
    'Amazon Prime Video': VideoLibrary, //done
    'Hotstar': Star, //done
    'SonyLIV': Tv, //done
    'ZEE5': OndemandVideo, //done
    'JioCinema': SmartDisplay, //done
    'YouTube': PlayArrow, //done
    'ALTBalaji': Theaters,
    'MX Player': PlayCircle, //done
    'Sun NXT': Cast, //done
    'Eros Now': Videocam, //skip
    'Aha': Movie, //done
    'Voot': ConnectedTv, //done
    'Apple TV+': Tv, //done
    'Hoichoi': Movie, //done
    'Discovery+': LiveTv, //done
    'Lionsgate Play': Theaters, //done
    'Hungama Play': VideoLibrary,
    'Planet Marathi': OndemandVideo,
    'Saina Play': SmartDisplay,
    'ShemarooMe': PlayCircle,
    'Ullu': Videocam,
    'BIGFlix': Movie,
    'Chaupal': Cast,
    'ETV Win': Tv, //done
    'JioHotstar': Star //done
  };
  
  // Check if PNG icon exists for this platform
  const pngIcon = pngIcons[platform];
  
  if (pngIcon) {
    return (
      <img 
        src={pngIcon} 
        alt={platform}
        style={{ 
          width: size, 
          height: size, 
          objectFit: 'contain',
          ...props.style 
        }} 
        {...props} 
      />
    );
  }
  
  // Fallback to Material icon
  const IconComponent = iconMap[platform] || PlayArrow;
  return <IconComponent style={{ fontSize: size, color, ...props.style }} {...props} />;
}

/**
 * Get OTT platform information from URL with theme-aware colors
 * @param {string} url - The OTT platform URL
 * @param {Object} theme - MUI theme object (optional)
 * @returns {Object} Platform object with name, color, and theme colors
 */
export function getOTTPlatform(url, theme = null) {
  if (!url) return { 
    name: "Watch", 
    color: "#6366f1",
    lightColor: "#6366f1",
    darkColor: "#818cf8"
  };
  
  const rules = [
    { 
      match: /netflix\.com/, 
      name: "Netflix", 
      color: "#E50914",
      lightColor: "#E50914",
      darkColor: "#FF1E2D"
    },
    { 
      match: /primevideo\.com|amazon\./, 
      name: "Amazon Prime Video", 
      color: "#00A8E1",
      lightColor: "#00A8E1",
      darkColor: "#1DB5E8"
    },
    { 
      match: /hotstar\.com|disneyplus\.com/, 
      name: "JioHotstar", 
      color: "#0F1419",
      lightColor: "#0F1419",
      darkColor: "#1F2937"
    },
    { 
      match: /jiohotstar\.com/, 
      name: "JioHotstar", 
      color: "#0F1419",
      lightColor: "#0F1419",
      darkColor: "#1F2937"
    },
    { 
      match: /sonyliv\.com/, 
      name: "SonyLIV", 
      color: "#0078D4",
      lightColor: "#0078D4",
      darkColor: "#1E88E5"
    },
    { 
      match: /zee5\.com/, 
      name: "ZEE5", 
      color: "#6A1B9A",
      lightColor: "#6A1B9A",
      darkColor: "#8E24AA"
    },
    { 
      match: /jio\.com|jiocinema\.com/, 
      name: "JioCinema", 
      color: "#8B2CF5",
      lightColor: "#8B2CF5",
      darkColor: "#A855F7"
    },
    { 
      match: /altbalaji\.com/, 
      name: "ALTBalaji", 
      color: "#FF4081",
      lightColor: "#FF4081",
      darkColor: "#FF6BA3"
    },
    { 
      match: /mx\.com|mxplayer\.in/, 
      name: "MX Player", 
      color: "#0099FF",
      lightColor: "#0099FF",
      darkColor: "#33AAFF"
    },
    { 
      match: /sunnxt\.com/, 
      name: "Sun NXT", 
      color: "#FF6B35",
      lightColor: "#FF6B35",
      darkColor: "#FF8A65"
    },
    { 
      match: /erosnow\.com/, 
      name: "Eros Now", 
      color: "#D32F2F",
      lightColor: "#D32F2F",
      darkColor: "#EF5350"
    },
    { 
      match: /aha\.video/, 
      name: "Aha", 
      color: "#F46C33",
      lightColor: "#F46C33",
      darkColor: "#FB923C"
    },
    { 
      match: /voot\.com/, 
      name: "Voot", 
      color: "#FF6600",
      lightColor: "#FF6600",
      darkColor: "#FF8533"
    },
    { 
      match: /tv\.apple\.com/, 
      name: "Apple TV+", 
      color: "#000000",
      lightColor: "#000000",
      darkColor: "#333333"
    },
    { 
      match: /hoichoi\.tv/, 
      name: "Hoichoi", 
      color: "#FF5722",
      lightColor: "#FF5722",
      darkColor: "#FF7043"
    },
    { 
      match: /discoveryplus\.in|discovery\.com/, 
      name: "Discovery+", 
      color: "#0077C8",
      lightColor: "#0077C8",
      darkColor: "#1E88E5"
    },
    { 
      match: /lionsgate\.com|lionsgateplay\.com/, 
      name: "Lionsgate Play", 
      color: "#FFD700",
      lightColor: "#FFD700",
      darkColor: "#FFEB3B"
    },
    { 
      match: /hungama\.com|hungamaplay\.com/, 
      name: "Hungama Play", 
      color: "#E91E63",
      lightColor: "#E91E63",
      darkColor: "#F06292"
    },
    { 
      match: /planetmarathi\.com/, 
      name: "Planet Marathi", 
      color: "#FF9800",
      lightColor: "#FF9800",
      darkColor: "#FFB74D"
    },
    { 
      match: /sainaplay\.com/, 
      name: "Saina Play", 
      color: "#4CAF50",
      lightColor: "#4CAF50",
      darkColor: "#66BB6A"
    },
    { 
      match: /shemaroome\.com/, 
      name: "ShemarooMe", 
      color: "#9C27B0",
      lightColor: "#9C27B0",
      darkColor: "#BA68C8"
    },
    { 
      match: /ullu\.app/, 
      name: "Ullu", 
      color: "#FF5722",
      lightColor: "#FF5722",
      darkColor: "#FF7043"
    },
    { 
      match: /bigflix\.com/, 
      name: "BIGFlix", 
      color: "#2196F3",
      lightColor: "#2196F3",
      darkColor: "#64B5F6"
    },
    { 
      match: /chaupal\.com/, 
      name: "Chaupal", 
      color: "#795548",
      lightColor: "#795548",
      darkColor: "#A1887F"
    },
    { 
      match: /etvwin\.com/, 
      name: "ETV Win", 
      color: "#E91E63",
      lightColor: "#E91E63",
      darkColor: "#F06292"
    },
    { 
      match: /youtube\.com/, 
      name: "YouTube", 
      color: "#FF0000",
      lightColor: "#FF0000",
      darkColor: "#FF4444"
    }
  ];
  
  for (let rule of rules) {
    if (rule.match.test(url)) {
      const platform = { ...rule };
      // Use theme-aware color if theme is provided
      if (theme) {
        platform.themeColor = theme.palette.mode === 'dark' ? platform.darkColor : platform.lightColor;
      }
      return platform;
    }
  }
  
  return { 
    name: "Watch", 
    color: "#6366f1",
    lightColor: "#6366f1",
    darkColor: "#818cf8",
    themeColor: theme?.palette.mode === 'dark' ? "#818cf8" : "#6366f1"
  };
}

/**
 * Get all supported OTT platforms with theme-aware colors
 * @param {Object} theme - MUI theme object (optional)
 * @returns {Array} Array of platform objects
 */
export function getAllOTTPlatforms(theme = null) {
  const platforms = [
    { name: "SonyLIV", color: "#0078D4", lightColor: "#0078D4", darkColor: "#1E88E5", domain: "sonyliv.com" },
    { name: "Amazon Prime Video", color: "#00A8E1", lightColor: "#00A8E1", darkColor: "#1DB5E8", domain: "primevideo.com" },
    { name: "Netflix", color: "#E50914", lightColor: "#E50914", darkColor: "#FF1E2D", domain: "netflix.com" },
    { name: "ZEE5", color: "#6A1B9A", lightColor: "#6A1B9A", darkColor: "#8E24AA", domain: "zee5.com" },
    { name: "Hotstar", color: "#0F1419", lightColor: "#0F1419", darkColor: "#1F2937", domain: "hotstar.com" },
    { name: "ALTBalaji", color: "#FF4081", lightColor: "#FF4081", darkColor: "#FF6BA3", domain: "altbalaji.com" },
    { name: "MX Player", color: "#0099FF", lightColor: "#0099FF", darkColor: "#33AAFF", domain: "mxplayer.in" },
    { name: "JioCinema", color: "#8B2CF5", lightColor: "#8B2CF5", darkColor: "#A855F7", domain: "jiocinema.com" },
    { name: "Sun NXT", color: "#FF6B35", lightColor: "#FF6B35", darkColor: "#FF8A65", domain: "sunnxt.com" },
    { name: "Eros Now", color: "#D32F2F", lightColor: "#D32F2F", darkColor: "#EF5350", domain: "erosnow.com" },
    { name: "Aha", color: "#F46C33", lightColor: "#F46C33", darkColor: "#FB923C", domain: "aha.video" },
    { name: "Voot", color: "#FF6600", lightColor: "#FF6600", darkColor: "#FF8533", domain: "voot.com" },
    { name: "Apple TV+", color: "#000000", lightColor: "#000000", darkColor: "#333333", domain: "tv.apple.com" },
    { name: "Hoichoi", color: "#FF5722", lightColor: "#FF5722", darkColor: "#FF7043", domain: "hoichoi.tv" },
    { name: "Discovery+", color: "#0077C8", lightColor: "#0077C8", darkColor: "#1E88E5", domain: "discoveryplus.in" },
    { name: "Lionsgate Play", color: "#FFD700", lightColor: "#FFD700", darkColor: "#FFEB3B", domain: "lionsgate.com" },
    { name: "Hungama Play", color: "#E91E63", lightColor: "#E91E63", darkColor: "#F06292", domain: "hungama.com" },
    { name: "Planet Marathi", color: "#FF9800", lightColor: "#FF9800", darkColor: "#FFB74D", domain: "planetmarathi.com" },
    { name: "Saina Play", color: "#4CAF50", lightColor: "#4CAF50", darkColor: "#66BB6A", domain: "sainaplay.com" },
    { name: "ShemarooMe", color: "#9C27B0", lightColor: "#9C27B0", darkColor: "#BA68C8", domain: "shemaroome.com" },
    { name: "Ullu", color: "#FF5722", lightColor: "#FF5722", darkColor: "#FF7043", domain: "ullu.app" },
    { name: "BIGFlix", color: "#2196F3", lightColor: "#2196F3", darkColor: "#64B5F6", domain: "bigflix.com" },
    { name: "Chaupal", color: "#795548", lightColor: "#795548", darkColor: "#A1887F", domain: "chaupal.com" },
    { name: "JioHotstar", color: "#0F1419", lightColor: "#0F1419", darkColor: "#1F2937", domain: "jiohotstar.com" },
    { name: "YouTube", color: "#FF0000", lightColor: "#FF0000", darkColor: "#FF4444", domain: "youtube.com" },
  ];
  
  // Add theme-aware colors if theme is provided
  if (theme) {
    return platforms.map(platform => ({
      ...platform,
      themeColor: theme.palette.mode === 'dark' ? platform.darkColor : platform.lightColor
    }));
  }
  
  return platforms;
}

/**
 * Validate if URL is a supported OTT platform
 * @param {string} url - The URL to validate
 * @returns {boolean} True if supported platform
 */
export function isValidOTTPlatform(url) {
  if (!url) return false;
  const platform = getOTTPlatform(url);
  return platform.name !== "Watch";
}

/**
 * Get platform button styles with Option 1 design (horizontal scrollable pills)
 * @param {Object} platform - Platform object from getOTTPlatform
 * @param {Object} theme - MUI theme object
 * @returns {Object} Button styles
 */
export function getPlatformButtonStyles(platform, theme) {
  const baseColor = platform.themeColor || platform.color;
  
  return {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1,
    borderRadius: '50px',
    fontSize: '0.875rem',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    minWidth: 'auto',
    height: '36px',
    bgcolor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.3)',
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#334155',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 16px rgba(0, 0, 0, 0.4)'
        : '0 4px 16px rgba(0, 0, 0, 0.15)',
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(255, 255, 255, 0.5)',
      bgcolor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(255, 255, 255, 0.95)',
      '&::after': {
        opacity: 0.05
      }
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: baseColor,
      opacity: 0,
      transition: 'opacity 0.3s ease'
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      minWidth: 'auto',
      '& img': {
        filter: theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none'
      }
    },
    '& .MuiButton-endIcon': {
      margin: 0,
      marginLeft: '4px',
      minWidth: 'auto',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      '&.show-on-hover': {
        opacity: 1
      }
    }
  };
}

/**
 * Color indicator component for Option 1 design
 * @param {Object} props - Component props
 * @param {string} props.color - Platform color
 * @param {number} props.size - Indicator size (default: 8)
 * @returns {React.Component} Color indicator component
 */
export function PlatformColorIndicator({ color, size = 8 }) {
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0
  };
}

/**
 * External link icon component for Option 1 design
 * @param {Object} props - Component props
 * @param {Object} theme - MUI theme object
 * @returns {React.Component} External link icon
 */
export function PlatformExternalIcon({ theme, size = 12, className, sx }) {
  return (
    <OpenInNew 
      className={className}
      sx={{ 
        fontSize: size,
        color: theme?.palette.mode === 'dark' ? '#fbbf24' : '#2563eb',
        ...sx
      }} 
    />
  );
}